import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CalendarCheck, CheckCircle2, FileText, Loader2, Lock, MessageSquareQuote, Nfc, QrCode, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { formatRange } from "@/lib/dates";
import { useAuth } from "@/lib/auth";
import { VendorAvatar } from "@/components/Brand";
import { OtpStep } from "@/components/OtpStep";
import { GoogleButton, OrDivider } from "@/components/GoogleButton";
import { startGoogleLogin } from "@/pages/AuthCallback";
import { LogoutButton } from "@/components/LogoutButton";
import { TypeProfileForm } from "@/components/TypeProfileForm";
import { ShareSuccess } from "@/components/ShareSuccess";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const PERKS = [
  { icon: FileText, label: "Brochure" },
  { icon: Share2, label: "Socials" },
  { icon: MessageSquareQuote, label: "Quote" },
  { icon: CalendarCheck, label: "Booking" },
];

export default function Booth({ preregister = false }: { preregister?: boolean }) {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const source = preregister ? "preregister" : params.get("src") === "nfc" ? "nfc" : "qr";
  const nav = useNavigate();
  const { customer, signIn } = useAuth();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", share_consent: false, marketing_consent: false });
  const [step, setStep] = useState<"form" | "otp" | "profile" | "done">("form");
  const [busy, setBusy] = useState(false);
  const [leadId, setLeadId] = useState("");
  const [profile, setProfile] = useState<any>(null);
  const auto = useRef(false);

  useEffect(() => {
    api.get(`/public/booth/${slug}`, { params: { src: source } }).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [slug, source]);

  useEffect(() => {
    if (customer?.user) setForm((f) => ({ ...f, name: f.name || customer.user.name, email: f.email || customer.user.email }));
  }, [customer]);

  const afterClaim = (res: any) => {
    if (!data?.event_type) return nav(`/me/v/${res.lead_id}?new=1`);
    setLeadId(res.lead_id);
    if (res.profile?.complete) {
      toast.success(`Shared with ${data.vendor.name} — materials saved`);
      setStep("done");
    } else {
      setProfile(res.profile);
      setStep("profile");
    }
  };

  useEffect(() => {
    const u = customer?.user;
    if (!u || !data?.event_type || !data.my_profile?.complete || auto.current) return;
    auto.current = true;
    setBusy(true);
    api.post(`/public/booth/${slug}/claim`, { name: u.name || u.email.split("@")[0], email: u.email, phone: "", share_consent: true, marketing_consent: false, source })
      .then((r) => afterClaim(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, data]);

  const claim = async () => {
    const { data: res } = await api.post(`/public/booth/${slug}/claim`, { ...form, source });
    if (res.verified) afterClaim(res);
    else setStep("otp");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.share_consent) return toast.error("Please tick the consent box to share your details");
    setBusy(true);
    try {
      await claim();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code: string) => {
    try {
      const { data: res } = await api.post(`/public/booth/${slug}/verify`, { email: form.email, code });
      await signIn("customer", res.token);
      auto.current = true;
      afterClaim(res);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (error) return <div className="grid min-h-screen place-items-center p-6 text-center text-slate-600" data-testid="booth-error">{error}</div>;
  if (!data) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  const { vendor, event, consent, event_type } = data;
  const autoSharing = busy && step === "form" && auto.current;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="h-28 w-full" style={{ background: `linear-gradient(135deg, ${vendor.brand_color || "#E05A33"}, #1C1917)` }} />
      <main className="mx-auto -mt-16 max-w-md px-4 pb-10">
        <div className="rise hard-shadow rounded-2xl border border-border bg-white p-6" data-testid="booth-card">
          <div className="flex items-start justify-between">
            <VendorAvatar vendor={vendor} size={64} />
            <span className="flex items-center gap-1.5 rounded-sm bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="booth-source-indicator">
              <span className="relative flex h-2 w-2"><span className="nfc-ping absolute h-2 w-2 rounded-full bg-emerald-500" /><span className="h-2 w-2 rounded-full bg-emerald-500" /></span>
              {preregister ? <CalendarCheck className="h-3.5 w-3.5" /> : source === "nfc" ? <Nfc className="h-3.5 w-3.5" /> : <QrCode className="h-3.5 w-3.5" />} {preregister ? "Pre-registration" : source === "nfc" ? "NFC tag" : "QR scan"}
            </span>
          </div>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight" data-testid="booth-vendor-name">{vendor.name}</h1>
          {vendor.tagline && <p className="mt-1 text-sm text-slate-600">{vendor.tagline}</p>}
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500" data-testid="booth-event-name">
            {event.name}{event.booth ? ` · Booth ${event.booth}` : ""}
          </p>
          {event.start_date && <p className="mt-1 text-sm font-medium text-slate-700" data-testid="booth-event-dates">{[formatRange(event.start_date, event.end_date), event.venue].filter(Boolean).join(" · ")}</p>}
          {preregister && (
            <p className="mt-3 rounded-sm bg-muted p-3 text-sm text-slate-700" data-testid="prereg-intro">
              Register before the event. Get the materials now, then scan the booth QR code on the day to check in.
            </p>
          )}
          <div className="mt-5 grid grid-cols-4 gap-2">
            {PERKS.map((p) => (
              <div key={p.label} className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 py-3 text-[11px] font-semibold text-slate-600">
                <p.icon className="h-4 w-4 text-emerald-600" /> {p.label}
              </div>
            ))}
          </div>
        </div>

        <div className="rise rise-1 mt-4 rounded-2xl border bg-white p-6">
          {step === "done" ? <ShareSuccess leadId={leadId} /> : step === "profile" ? (
            <div className="space-y-4" data-testid="booth-profile-step">
              <div>
                <p className="flex items-center gap-1.5 text-sm font-bold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> {vendor.name} has your contact</p>
                <p className="mt-1 text-sm text-slate-600">Answer these {event_type.name} questions once. Next time you scan a booth at a {event_type.name.toLowerCase()}, your answers are shared instantly. No forms to fill in again.</p>
              </div>
              <TypeProfileForm eventType={event_type} answers={profile?.answers} consented={!!profile?.consented} consentText={event_type.consent_text}
                onlyIds={profile?.consented ? profile.missing : undefined} submitLabel="Save & share"
                onSaved={() => { toast.success(`Shared with ${vendor.name} — materials saved`); setStep("done"); }} />
            </div>
          ) : autoSharing ? (
            <p className="flex items-center justify-center gap-2 py-6 text-sm font-semibold text-slate-600" data-testid="booth-auto-sharing"><Loader2 className="h-4 w-4 animate-spin" /> Sharing your {event_type?.name} profile…</p>
          ) : step === "form" ? (
            <form onSubmit={submit} className="space-y-4" data-testid="booth-claim-form">
              <p className="text-sm font-semibold">{preregister ? "Register for this event" : "Share your contact to unlock everything"}</p>
              {!customer?.user && (
                <>
                  <GoogleButton onClick={() => startGoogleLogin("customer", `/${preregister ? "r" : "t"}/${slug}${source === "nfc" ? "?src=nfc" : ""}`)} label="Fill in with Google" testid="booth-google-button" />
                  <OrDivider />
                </>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="booth-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="booth-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone <span className="font-normal text-slate-400">(optional)</span></Label>
                <Input id="phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" data-testid="booth-phone-input" />
              </div>
              <label className="flex gap-3 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 text-xs leading-relaxed text-slate-700">
                <Checkbox checked={form.share_consent} onCheckedChange={(v) => setForm({ ...form, share_consent: !!v })} className="mt-0.5" data-testid="booth-share-consent-checkbox" />
                <span><b>Required.</b> {consent.share_text}</span>
              </label>
              <label className="flex gap-3 rounded-xl border p-3 text-xs leading-relaxed text-slate-600">
                <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => setForm({ ...form, marketing_consent: !!v })} className="mt-0.5" data-testid="booth-marketing-consent-checkbox" />
                <span><b>Optional.</b> {consent.marketing_text}</span>
              </label>
              <Button type="submit" disabled={busy || !form.share_consent} className="hard-shadow h-12 w-full rounded-sm bg-emerald-600 text-base text-white hover:bg-emerald-700" data-testid="booth-claim-submit-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : preregister ? "Register now" : "Share & save this vendor"}
              </Button>
            </form>
          ) : (
            <OtpStep email={form.email} onVerify={verify} onResend={() => claim().catch((e) => toast.error(errMsg(e)))} onBack={() => setStep("form")} />
          )}
        </div>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-slate-500" data-testid="booth-trust-note">
          <Lock className="h-3.5 w-3.5" /> Your details go only to {vendor.name}. Consent record {consent.version}.
        </p>
        {customer?.user && (
          <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-500" data-testid="booth-signed-in-bar">
            Signed in as {customer.user.email}
            <LogoutButton kind="customer" testid="booth-logout-button" label="Not you? Log out" className="text-xs" />
          </div>
        )}
      </main>
    </div>
  );
}
